import { useState, useEffect, useCallback } from "react";
import React from "react";
import useAdminUserFetch from "../useAdminUserFetch";
import TotalUsers from "./TotalUsers";
import Filters from "./Filters";
import UserTable from "./UserTable";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import Loading from "../../../../utils/LoadingIndicator/Loading";
import { useAuth } from "../../../../utils/AuthContext";

function UserManagementComponent({ onSetActiveTab }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const itemsPerPage = 10;
  const { user: currentUser } = useAuth();

  const {
    users,
    isGettingUsers,
    usersError,
    setUsersQueryParams,
    getUsersWithParams,
    updateUser: updateUserStatusMutation,
    totalPages,
  } = useAdminUserFetch();

  // Debounced search function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch users with current parameters
  const fetchUsers = useCallback(
    (params) => {
      setUsersQueryParams(params);
      getUsersWithParams(params);
    },
    [setUsersQueryParams, getUsersWithParams]
  );

  // Debounced fetch function
  const debouncedFetch = useCallback(
    debounce((params) => fetchUsers(params), 300),
    [fetchUsers]
  );

  // Get current query parameters (removed sorting from backend params)
  const getCurrentParams = () => ({
    page: currentPage,
    limit: itemsPerPage,
    search: searchTerm,
    isDeleted:
      statusFilter === "all"
        ? undefined
        : statusFilter === "deleted"
        ? "true"
        : "false",
  });

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const params = { ...getCurrentParams(), page: newPage };
    fetchUsers(params);
  };

  // Handle search with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page

    const params = {
      ...getCurrentParams(),
      search: value,
      page: 1,
    };

    debouncedFetch(params);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setCurrentPage(1);

    const params = {
      ...getCurrentParams(),
      page: 1,
      isDeleted:
        value === "all" ? undefined : value === "deleted" ? "true" : "false",
    };

    fetchUsers(params);
  };

  // Handle sorting (client-side)
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
  };

  // Sort users client-side and filter out current user
  const sortedUsers = React.useMemo(() => {
    if (!users || users.length === 0) return [];

    // Filter out the current admin user to prevent self-deletion
    const filteredUsers = users.filter((user) => user._id !== currentUser?._id);

    return filteredUsers.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "email":
          aValue = a.email?.toLowerCase() || "";
          bValue = b.email?.toLowerCase() || "";
          break;
        case "role":
          // Admin (1) vs User (0 or other)
          aValue = a.role === 1 ? "Admin" : "User";
          bValue = b.role === 1 ? "Admin" : "User";
          break;
        case "isDeleted":
          // Active (0) vs Deleted (1)
          aValue = a.isDeleted === 0 ? "Active" : "Deleted";
          bValue = b.isDeleted === 0 ? "Active" : "Deleted";
          break;
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      // Handle date comparison
      if (sortField === "createdAt") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string comparison
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDirection, currentUser]);

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Initial fetch and when dependencies change
  useEffect(() => {
    const params = getCurrentParams();
    fetchUsers(params);
  }, []); // Only run on mount

  const handleStatusChange = (userId, isDeleted) => {
    updateUserStatusMutation({ userId, userData: { isDeleted } });
  };

  // Clear search function
  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);

    const params = {
      ...getCurrentParams(),
      search: "",
      page: 1,
    };

    fetchUsers(params);
  };

  if (usersError) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <h2 className="text-red-500 text-xl">
          {usersError.message || "Failed to load users"}
        </h2>
      </div>
    );
  }

  return isGettingUsers ? (
    <Loading />
  ) : (
    <div className="w-full h-full px-4 pb-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <TotalUsers users={sortedUsers} onSetActiveTab={onSetActiveTab} />
        {/* Filters */}
        <Filters
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          handleStatusFilterChange={handleStatusFilterChange}
          clearSearch={clearSearch}
          statusFilter={statusFilter}
        />
        {/* Users Table */}
        <UserTable
          handleSort={handleSort}
          getSortIndicator={getSortIndicator}
          sortedUsers={sortedUsers}
          handleStatusChange={handleStatusChange}
          updateUserStatusMutation={updateUserStatusMutation}
        />
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {sortedUsers.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No users found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagementComponent;
